<!--
  The demo request over a view the lead can open, for the actions in it that
  stop short (see `openDemoRequest` in `leadRestriction.ts`). Same semi-dialog
  as on a locked view, but over the page itself, which stays as it is behind it:
  closing it returns to exactly where the lead was.
-->
<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { onKeyStroke } from '@vueuse/core';
import LeadDemoRequest from '@/components/leadBot/LeadDemoRequest.vue';
import { useLeadRestriction } from '@/components/leadBot/leadRestriction';

const route = useRoute();
const { isRestricted, demoRequestOpen, closeDemoRequest } = useLeadRestriction();

const request = ref<InstanceType<typeof LeadDemoRequest> | null>(null);
const isShown = () => isRestricted.value && demoRequestOpen.value;

/** What had the focus before it opened, e.g. the action that opened it */
let opener: HTMLElement | null = null;

/*
 * Moves the focus into it, so the keyboard doesn't stay on the page behind it (`GenericWrapper` makes that inert
 * meanwhile), and back to where it was once closed.
 */
watch(() => isShown(), async (shown) => {
  if (shown) {
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    await nextTick();
    (request.value?.$el as HTMLElement | undefined)?.focus();
    return;
  }

  const restoreTo = opener;
  opener = null;
  // After the page behind it is no longer inert
  await nextTick();
  if (restoreTo?.isConnected) restoreTo.focus();
});

watch(() => route.fullPath, closeDemoRequest);
onKeyStroke('Escape', () => {
  if (isShown()) closeDemoRequest();
});
</script>

<template>
  <div v-if="isShown()" data-cy="lead-demo-request-overlay" class="absolute inset-0 z-30 flex">
    <div aria-hidden="true" class="absolute inset-0 bg-primary/40 backdrop-blur-[2px]" />
    <!--
      `my-auto` centers it while there's room. Unlike centering through the flex container, a taller one
      stays scrollable to its top. A click next to it closes it, as on the backdrop.
    -->
    <div class="relative flex flex-1 items-start justify-center overflow-y-auto px-4 py-10" @click.self="closeDemoRequest">
      <LeadDemoRequest
        ref="request"
        tabindex="-1"
        class="my-auto shrink-0 outline-none"
        in-view
        @close="closeDemoRequest"
      />
    </div>
  </div>
</template>
